'use client';

import { imageService } from '@repo/ui/lib/services/image.service';
import { Card, CardContent } from '@repo/ui/components/ui/card';
import type { EssenceIllustrationModel } from './essence-illustration-model';

interface EssenceIllustrationPageProps {
  readonly essences: EssenceIllustrationModel[];
}

interface SingleIllustrationProps {
  readonly model: EssenceIllustrationModel;
}

function SingleEssenceIllustration({ model }: SingleIllustrationProps) {
  const resolvedUrl = model.imageUrl
    ? imageService.getPrimaryImage(model.imageUrl, 'espece')
    : null;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {resolvedUrl ? (
          <img
            src={resolvedUrl}
            alt={`Illustration ${model.latinName}`}
            className="h-64 w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-64 items-center justify-center bg-muted text-muted-foreground">
            Illustration non disponible
          </div>
        )}
        <div className="space-y-1 p-4">
          <p className="font-semibold italic">{model.latinName}</p>
          {model.frenchName ? (
            <p className="text-sm text-muted-foreground">{model.frenchName}</p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Displays essence (species) illustrations for an inventory sheet.
 * Covers Arbre, Alignement, and Espace boisé inventory types.
 */
export function EssenceIllustrationPage({ essences }: EssenceIllustrationPageProps) {
  if (essences.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed p-12 text-muted-foreground">
        Aucune illustration d&apos;essence disponible
      </div>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {essences.map((model) => (
        <SingleEssenceIllustration key={model.latinName} model={model} />
      ))}
    </div>
  );
}
